const FILTERED_PARAM_VALUE = '[filtered]';

// query/hash params that may carry PII or single-use secrets: email change
// links, team invitations, oauth callbacks, notification confirmations
// (confirmation_data embeds the email in plain text), unsubscribe links
const SENSITIVE_URL_PARAMS = [
  'email',
  // pseudonymous but still tied to a user — scrub it too (email hashing lands
  // in the confirmation/invitation links via BE-1)
  'email_hash',
  'token',
  'code',
  'state',
  'confirmation_data',
  'address',
  'tgAuthResult',
];

const scrubParams = (params: URLSearchParams): boolean => {
  let isScrubbed = false;

  SENSITIVE_URL_PARAMS.forEach(name => {
    if (params.has(name)) {
      params.set(name, FILTERED_PARAM_VALUE);
      isScrubbed = true;
    }
  });

  return isScrubbed;
};

// a fragment can be a bare query ('#a=1'), a hash-router path carrying a query
// ('#/route?a=1'), or a plain anchor ('#section'). Only the query portion holds
// parseable params, so preserve anything before the '?' rather than feeding the
// whole fragment to URLSearchParams (which would mis-key '/route?a' and both
// miss the real params and mangle the path).
const scrubHash = (hash: string): { isScrubbed: boolean; value: string } => {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const queryIndex = raw.indexOf('?');
  const prefix = queryIndex === -1 ? '' : raw.slice(0, queryIndex);
  const query = queryIndex === -1 ? raw : raw.slice(queryIndex + 1);

  const params = new URLSearchParams(query);

  if (!scrubParams(params)) {
    return { isScrubbed: false, value: hash };
  }

  return {
    isScrubbed: true,
    value: `#${prefix ? `${prefix}?` : ''}${params.toString()}`,
  };
};

export const scrubSensitiveUrlParams = (rawUrl: string): string => {
  // fast path: without a query or fragment there is nothing to scrub, so skip
  // the URL parsing entirely — this runs on every Sentry breadcrumb, most of
  // which are plain endpoint/route URLs with neither
  if (!rawUrl.includes('?') && !rawUrl.includes('#')) {
    return rawUrl;
  }

  try {
    let url: URL;
    let isAbsolute: boolean;

    try {
      // succeeds only for absolute URLs, of any scheme (http(s), wss, …) —
      // so non-http schemes keep their scheme+host rather than being reduced
      // to an origin-less path
      url = new URL(rawUrl);
      isAbsolute = true;
    } catch {
      // relative URL: resolve against the current origin to parse it, then
      // return it in its original relative form
      url = new URL(rawUrl, window.location.origin);
      isAbsolute = false;
    }

    const isSearchScrubbed = scrubParams(url.searchParams);
    const { isScrubbed: isHashScrubbed, value: scrubbedHash } = scrubHash(
      url.hash,
    );

    if (!isSearchScrubbed && !isHashScrubbed) {
      return rawUrl;
    }

    // url.search already reflects the scrubbed params (URLSearchParams is
    // linked to the URL object); pair it with the scrubbed hash, dropping the
    // synthetic origin for URLs that were originally relative
    const finalHash = isHashScrubbed ? scrubbedHash : url.hash;

    return isAbsolute
      ? `${url.origin}${url.pathname}${url.search}${finalHash}`
      : `${url.pathname}${url.search}${finalHash}`;
  } catch {
    return rawUrl;
  }
};
